<script>
  import { onMount } from 'svelte';
  import { navigate as goto } from 'svelte-routing';
  import ParticipantDetails from '$lib/ParticipantDetails.svelte';
  import Pagination from '$lib/Pagination.svelte';
  import FeatureInfo from '$lib/FeatureInfo.svelte';
  import { selectedStudy, selectedSite, selectedParticipant } from '$stores/selectionStore';
  import { FontAwesomeIcon } from '@fortawesome/svelte-fontawesome';
  import { faRotate } from '@fortawesome/pro-light-svg-icons';
  // Using local API endpoints instead of direct API access
  const API_BASE_URL = '/api/proxy';
  const demoMode = import.meta.env.VITE_DEMO_MODE === 'true';

  import { formatToLocalDateTime } from '$utils/dateUtils.js';
  import { exportToExcel, generateExportFilename } from '$utils/export.js';
  import Toolbar from '$lib/Toolbar.svelte';
  import Footer from '$lib/Footer.svelte';
  import StudyHeader from '$lib/StudyHeader.svelte';
  import StudyDetails from '$lib/StudyDetails.svelte';
  import SiteDetails from '$lib/SiteDetails.svelte';

  let messages = [];
  let resultCount = 0;
  let currentPage = 1;
  let totalPages = 1;
  let limit = 20;
  let isLoading = true;
  let selectedType = 'ALL';
  let selectedMessageType = 'CGMRECHECK';
  let isExporting = false;

  // Add message type options
  const messageTypes = [
    { code: 'ALL', label: 'All' },
    { code: 'MSGREMINDER', label: 'Reminder' },
    { code: 'CGMRECHECK', label: 'CGM Recheck' },
    { code: 'MSGALERT', label: 'Alert' },
    { code: 'MSGCGMRECHECK', label: 'Recheck' },
  ];

  onMount(async () => {
    if ($selectedStudy) {
      fetchMessages(1);
    }
  });

  // Subscribe to selectedStudy changes
  $: {
    if ($selectedStudy) {
      fetchMessages(1);
    }
  }

  async function fetchMessages(page = 1) {
    try {
      isLoading = true;
      let url = `${API_BASE_URL}/messages/studyMessages?page=${page}&pageSize=${limit}&studyGUID=${$selectedStudy._id}&subjectStatus=${selectedType}`;

      if ($selectedParticipant) {
        url += `&subjectGUID=${$selectedParticipant._id}`;
      }

      if ($selectedSite) {
        url += `&siteGUID=${$selectedSite._id}`;
      }

      if (selectedMessageType !== 'ALL') {
        url += `&messageType=${selectedMessageType}`;
      }

      const res = await fetch(url);

      if (!res.ok) {
        throw new Error('Failed to fetch messages');
      }

      const responseData = await res.json();

      resultCount = responseData.totalCount || 0;
      currentPage = responseData.page || 1;
      totalPages = responseData.totalPages || 1;

      messages = responseData.data || [];
    } catch (err) {
      console.error('Error fetching messages:', err);
      messages = [];
    } finally {
      isLoading = false;
    }
  }

  function handleRefresh() {
    fetchMessages(1);
  }

  function handlePageChange(newPage) {
    if (newPage > 0 && newPage <= totalPages) {
      fetchMessages(newPage);
    }
  }

  async function fetchAllMessages() {
    try {
      let url = `${API_BASE_URL}/messages/studyMessages?page=1&pageSize=999999&studyGUID=${$selectedStudy._id}&subjectStatus=${selectedType}`;

      if ($selectedParticipant) {
        url += `&subjectGUID=${$selectedParticipant._id}`;
      }

      if ($selectedSite) {
        url += `&siteGUID=${$selectedSite._id}`;
      }

      if (selectedMessageType !== 'ALL') {
        url += `&messageType=${selectedMessageType}`;
      }

      const res = await fetch(url);

      if (!res.ok) {
        throw new Error('Failed to fetch all messages');
      }

      const result = await res.json();
      return result.data || [];
    } catch (err) {
      console.error('Error fetching all messages:', err);
      return [];
    }
  }

  async function handleExportExcel() {
    try {
      isExporting = true;
      const allMessages = await fetchAllMessages();

      const now = new Date();
      const dateStr = now
        .toLocaleString('en-US', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          hour12: false,
        })
        .replace(/,/g, '')
        .replace(/\s+/g, '')
        .replace(/:/g, '')
        .replace(/(\d{2})([A-Za-z]{3})(\d{4})_(\d{2})(\d{2})/, '$2$1$3_$4$5');

      const filename = generateExportFilename({
        protocolNumber: $selectedStudy?.protocolNumber,
        reportType: 'Messages'
      });

      const formattedData = allMessages.map((item) => ({
        Protocol: demoMode ? 'DEMO-0001' : item.protocol || '',
        Site: demoMode ? 'SITE-0001' : item.siteId || '',
        Subject: item.subjectId || '',
        Status: item.subjectStatus || '',
        'Sent Time (Local)': item.sent || '',
        'Sent Time (UTC)': item.sentUTC || '',
        Timezone: item.timezone || '',
        Title: item.title || '',
        Message: item.message || '',
      }));

      await exportToExcel(formattedData, filename);
    } catch (error) {
      console.error('Error exporting to Excel:', error);
    } finally {
      isExporting = false;
    }
  }
</script>

<!-- Add loading overlay for export -->
{#if isExporting}
  <div class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <div class="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-lg flex flex-col items-center">
      <div class="loader mb-4"></div>
      <p class="text-sm text-gray-700 dark:text-gray-300">Preparing Excel export...</p>
    </div>
  </div>
{/if}

<StudyHeader />

<FeatureInfo 
  title="Alerts/Messages Report" 
  description="This page displays all alerts/messages sent to the selected participant or all participants in the study or selected site. You can filter by type to view specific communications. 
  The report includes timing, content, and participant details. Data can be exported to Excel for further analysis."
  marginBottom="mb-0"
/>

{#if $selectedParticipant}
  <ParticipantDetails showOptionsMenu={false} />
{:else if $selectedSite}
  <SiteDetails showOptionsMenu={false} />
{:else if $selectedStudy}
  <StudyDetails showOptionsMenu={false} />
{/if}

<!-- Title and Filters -->
<Toolbar title="Alerts/Messages Report" itemCountTitle="Alerts/Messages" itemCount={resultCount}>
  {#if !$selectedParticipant}
    <div class="flex items-center gap-2">
      Status:
      <select
        bind:value={selectedType}
        on:change={() => fetchMessages(1)}
        class="form-select h-6 w-52 border px-2 p-0 rounded text-sm dark:bg-gray-700 dark:border-gray-600 text-gray-800 dark:text-gray-100 bg-white appearance-none"
      >
        <option value="ALL">All</option>
        <option value="COMPD">Completed</option>
        <option value="CONS">Consented</option>
        <option value="DISC">Discontinued</option>
        <option value="INIT">Initiated</option>
        <option value="SCFA">Screen Failed</option>
        <option value="SCRE">Screened</option>
        <option value="ENRO">Screened/Enrolled</option>
      </select>
    </div>
  {/if}
  <div class="flex items-center gap-2">
    <span>Type:</span>
    <select
      bind:value={selectedMessageType}
      on:change={() => fetchMessages(1)}
      class="form-select h-6 w-44 border px-2 p-0 rounded text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 bg-white appearance-none"
    >
      {#each messageTypes as type}
        <option value={type.code}>{type.label}</option>
      {/each}
    </select>
  </div>
  <div class="flex items-baseline">
    <button on:click={handleRefresh} class="pagination" title="Refresh">
      <FontAwesomeIcon icon={faRotate} />
    </button>
  </div>
</Toolbar>

{#if isLoading}
  <div class="flex flex-col justify-center items-center h-64 bg-stone-100/50 dark:bg-gray-725 mx-1">
    <div class="loader mb-4"></div>
    <p class="text-xs justify-center text-gray-700 dark:text-gray-300">preparing messages report</p>
  </div>
{:else}
  <div class="px-1 pt-0">
    <table class="min-w-full border-collapse table-auto">
      <thead>
        <tr class="text-sm text-black dark:text-slate-300 bg-stone-200/50 dark:bg-gray-800/90">
          <th class="px-4 py-2 text-left bold">Site</th>
          <th class="px-4 py-2 text-left bold">Subject</th>
          <th class="px-4 py-2 text-left bold">Status</th>
          <th class="px-4 py-2 text-left bold">Sent Time (Local)</th>
          <th class="px-4 py-2 text-left bold">Timezone</th>
          <th class="px-4 py-2 text-left bold">Title</th>
          <th class="px-4 py-2 text-left bold">Message</th>
        </tr>
      </thead>
      <tbody>
        {#each messages as message, index}
          <tr
            class="text-sm hover:bg-white dark:hover:bg-gray-775 text-gray-800 dark:text-slate-300 {index %
              2 ===
            0
              ? 'bg-stone-50 dark:bg-gray-725'
              : 'bg-stone-75 dark:bg-gray-750'}"
          >
            <td class="px-4 py-2">{message.siteId ?? 'n/a'}</td>
            <td class="px-4 py-2">{message.subjectId ?? 'n/a'}</td>
            <td class="px-4 py-2">{message.subjectStatus ?? 'n/a'}</td>
            <td class="px-4 py-2">{message.sent ?? 'n/a'}</td>
            <td class="px-4 py-2">{message.timezone ?? 'n/a'}</td>
            <td class="px-4 py-2">{message.title ?? 'n/a'}</td>
            <td class="px-4 py-2">{message.message ?? 'n/a'}</td>
          </tr>
        {/each}
      </tbody>
    </table>
  </div>
{/if}

<!-- Add Control Buttons -->
<Footer>
  <Pagination {currentPage} {totalPages} onPageChange={handlePageChange} />
  <div>
    <button
      on:click={handleExportExcel}
      class="bg-stone-300 dark:bg-gray-600 text-black dark:text-white py-1 px-4 rounded-full text-xs"
      >Excel</button
    >
  </div>
</Footer>

<style>
  .loader {
    border: 2px solid #20669c;
    border-radius: 50%;
    border-top: 4px solid #98acb4;
    width: 32px;
    height: 32px;
    animation: spin 2s linear infinite;
  }

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }

  .fixed {
    position: fixed;
  }

  .inset-0 {
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
  }

  .bg-opacity-50 {
    --tw-bg-opacity: 0.5;
  }

  .z-50 {
    z-index: 50;
  }

  .shadow-lg {
    --tw-shadow: 0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1);
    --tw-shadow-colored: 0 10px 15px -3px var(--tw-shadow-color),
      0 4px 6px -4px var(--tw-shadow-color);
    box-shadow: var(--tw-ring-offset-shadow, 0 0 #0000), var(--tw-ring-shadow, 0 0 #0000),
      var(--tw-shadow);
  }
</style>
